import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { CustomTableHead, CustomTableHeadCell } from '../../../components/@extended/CustomTable';
import { Edit, Check, Delete } from '@mui/icons-material';
import CustomButton from '../../../components/@extended/CustomButton';
import moment from 'moment';
import {
  useAmendInvoiceMutation,
  useGenerateFianceInvoiceMutation,
  useGetInvoiceQuery,
  useLazyGetInvoiceQuery,
} from '../../../store/slices/apiSlice';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { openMessage } from '../../../store/slices/showMessageSlice';

const validationSchema = Yup.object().shape({
  amendmentType: Yup.string().required('Amendment type is required'),
  reason: Yup.string(),
});

const GenerateInvoice = ({ handleClose, isUpdate = false, isEditable = false, invoiceId, trialId,trialSiteId }) => {
  const [generateFianceInvoice, { isLoading }] = useGenerateFianceInvoiceMutation();
  const [amendInvoice, { isLoading: amendLoading }] = useAmendInvoiceMutation();

  const columns = useMemo(
    () => [
      { key: 'srno', label: 'Sr. No.', maxWidth: 80, align: 'center' },
      { key: 'description', label: 'Service Description', minWidth: 180, align: 'center' },
      { key: 'amount', label: 'Amount', minWidth: 80, align: 'center' },
      { key: 'quantity', label: 'UoQ', minWidth: 80, align: 'center' },
      { key: 'total', label: 'Total', minWidth: 80, align: 'center' },
      ...(isEditable ? [{ key: 'actions', label: '', minWidth: 140, align: 'center' }] : []),
    ],
    [isEditable]
  );

  const dispatch = useDispatch();

  const [getInvoice, { data: invoiceData }] = useLazyGetInvoiceQuery();

  useEffect(() => {
    // console.log('invoiceId', invoiceId);
    if (invoiceId) {
      getInvoice({ id: invoiceId });
    }
  }, [invoiceId]);

  const theme = useTheme();

  const selectedSite = useSelector((state) => state.finance?.currentSite || {});
  const sponsor = useSelector((state) => state.userDetails?.user || {});

  const siteInfo = useMemo(() => {
    if (!selectedSite?.siteEntity?.addditionalInfo) return {};
    try {
      return JSON.parse(selectedSite?.siteEntity?.addditionalInfo);
    } catch (error) {
      console.error('Error parsing site additional info:', error);
      return {};
    }
  }, [selectedSite]);

  // Initialize with default data or empty array
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [formData, setFormData] = useState({ description: '', amount: '', quantity: '', total: '' });

  // Initialize with either passed data or default values
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(moment().format('DD MMM YYYY'));

  const [deletedRows, setDeletedRows] = useState([]);

  // Load invoice data if provided in props
  useEffect(() => {
    try {
      if (invoiceData) {
        // Set invoice items from props data
        if (invoiceData.items && Array.isArray(invoiceData.items) && invoiceData.items.length > 0) {
          const formattedItems = invoiceData.items.map((item, index) => ({
            srno: index + 1,
            description: item.description || '',
            amount: Number(item.amount) || 0,
            quantity: Number(item.quantity) || 0,
            total: (Number(item.amount) || 0) * (Number(item.quantity) || 0),
            id: item?.id,
          }));
          setInvoiceItems(formattedItems);
        } else {
          // Set default empty item if no items provided
          setInvoiceItems([{ srno: 1, description: '', amount: 0, quantity: 0, total: 0 }]);
        }

        // Set invoice number and date if provided
        if (invoiceData?.invoiceDetails?.invoiceNumber) {
          setInvoiceNumber(invoiceData?.invoiceDetails?.invoiceNumber);
        } else {
          generateInvoiceNumber();
        }

        if (invoiceData.invoiceDate) {
          setInvoiceDate(moment(invoiceData.invoiceDate).format('DD MMM YYYY'));
        }
      } else {
        // Set default items if no data provided
        setInvoiceItems([
          { srno: 1, description: 'Service 1', amount: 100, quantity: 2, total: 200 },
          { srno: 2, description: 'Service 2', amount: 150, quantity: 1, total: 150 },
        ]);

        // Generate invoice number if not provided
        generateInvoiceNumber();
      }
    } catch (error) {
      console.error('Error setting up invoice data:', error);
      // Set failsafe defaults
      setInvoiceItems([{ srno: 1, description: '', amount: 0, quantity: 0, total: 0 }]);
      generateInvoiceNumber();
    }
  }, [invoiceData]);

  const generateInvoiceNumber = () => {
    const siteCode = selectedSite?.siteCode || 'INV';
    const datePart = moment().format('DDMMYYYYHHmm');
    const randomPart = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
    setInvoiceNumber(`${siteCode}-${datePart}-${randomPart}`);
  };

  const handleEdit = (index) => {
    if (!isEditable) return;

    setEditIndex(index);
    setFormData({ ...invoiceItems[index] });
  };

  const handleChange = (e, key) => {
    if (!isEditable) return;

    let value = e.target.value;

    // Validate numeric inputs
    if (key === 'amount' || key === 'quantity') {
      value = value === '' ? 0 : Number(value);
      if (isNaN(value) || value < 0) value = 0;
    }

    let updatedData = { ...formData, [key]: value };

    // Calculate total whenever amount or quantity changes
    if (key === 'amount' || key === 'quantity') {
      const amount = key === 'amount' ? value : Number(formData.amount) || 0;
      const quantity = key === 'quantity' ? value : Number(formData.quantity) || 0;
      updatedData.total = amount * quantity;
    }

    setFormData(updatedData);
  };

  const handleSave = () => {
    if (!isEditable || editIndex === null) return;

    // Validate form data before saving
    if (!formData.description || formData.description.trim() === '') {
      alert('Description cannot be empty');
      return;
    }

    const updatedItems = invoiceItems.map((item, index) =>
      index === editIndex
        ? {
            ...formData,
            description: formData.description.trim(),
            amount: Number(formData.amount) || 0,
            quantity: Number(formData.quantity) || 0,
            total: Number(formData.total) || 0,
          }
        : item
    );

    setInvoiceItems(updatedItems);
    setEditIndex(null);
  };

  const handleRemoveRow = (index) => {
    if (!isEditable) return;

    // Don't allow removing the last row
    if (invoiceItems.length <= 1) return;

    const item = invoiceItems.find((_, i) => i !== index);
    if (item?.id) {
      setDeletedRows([...deletedRows, item?.id]);
    }
    const updatedItems = invoiceItems.filter((_, i) => i !== index).map((item, i) => ({ ...item, srno: i + 1 })); // Update sr no

    setInvoiceItems(updatedItems);

    // Reset edit mode if the edited row was removed
    if (editIndex === index) {
      setEditIndex(null);
    } else if (editIndex !== null && editIndex > index) {
      // Adjust editIndex if a row above it was removed
      setEditIndex(editIndex - 1);
    }
  };

  const handleAddRow = () => {
    if (!isEditable) return;

    // If currently editing, save current changes first
    if (editIndex !== null) {
      handleSave();
    }

    const newItem = {
      srno: invoiceItems.length + 1,
      description: '',
      amount: 0,
      quantity: 0,
      total: 0,
    };

    setInvoiceItems([...invoiceItems, newItem]);
    setEditIndex(invoiceItems.length);
    setFormData(newItem);
  };

  const { subtotal, taxes, stripeCharges, transactionHandlingCost, totalAmountDue } = useMemo(() => {
    try {
      const subtotal = invoiceItems.reduce((acc, item) => acc + Number(item.total || 0), 0);
      const taxes = subtotal * 0.1;
      const stripeCharges = subtotal * 0.0025;
      const transactionHandlingCost = 1;
      const totalAmountDue = subtotal + taxes + stripeCharges + transactionHandlingCost;

      return {
        subtotal,
        taxes,
        stripeCharges,
        transactionHandlingCost,
        totalAmountDue,
      };
    } catch (error) {
      console.error('Error calculating totals:', error);
      return {
        subtotal: 0,
        taxes: 0,
        stripeCharges: 0,
        transactionHandlingCost: 0,
        totalAmountDue: 0,
      };
    }
  }, [invoiceItems]);

  const validateInvoice = () => {
    // Check if any item is missing required fields
    const invalidItems = invoiceItems.some(
      (item) => !item.description || item.description.trim() === '' || !item.amount || !item.quantity
    );

    if (invalidItems) {
      return false;
    }

    return true;
  };

  const handleGenerateInvoice = async (values, validateForm) => {
    if (!isEditable) return;

    // Validate all fields before submitting
    if (!validateInvoice()) {
      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: 'Please fill in all required fields for invoice items',
        })
      );

      return;
    }

    try {
      if (isUpdate) {
        if (invoiceData?.invoiceDetails?.status === 'APPROVED' || invoiceData?.invoiceDetails?.status === 'AMMENDED') {
          const formErrors = await validateForm();

          // Check if there are any validation errors
          if (Object.keys(formErrors).length > 0) {
            // There are validation errors
            const errorMessages = Object.values(formErrors).join(', ');
            dispatch(
              openMessage({
                messageSeverity: 'error',
                message: `Please fix the following errors: ${errorMessages}`,
              })
            );
            return;
          }
        }
        const body = {
          reason: values.reason || 'Changing price',
          amendmentType: values.amendmentType|| 'additional_charge' ,
          items: invoiceItems.map((i) => {
            return {
              id: i?.id,
              description: i?.description,
              amount: Number(i?.amount),
              quantity: Number(i?.quantity),
            };
          }),
          itemsToRemove: deletedRows,
        };

        const res = await amendInvoice({ id: invoiceId, body });
        if (res.data) {
          handleClose();
        } else if (res.error) {
          dispatch(
            openMessage({
              messageSeverity: 'error',
              message: `Error generating invoice: ${res.error.message || 'Unknown error'}`,
            })
          );
        }
      } else {
        const body = {
          invoiceNumber: invoiceNumber,
          invoiceDate: moment().format('YYYY-MM-DD'),
          dueDate: moment().add(3, 'days').format('YYYY-MM-DD'),
          recipientEntityId: sponsor?.financeDetail?.entityId,
          issuerEntityId: selectedSite?.siteEntityId,
          amount: totalAmountDue,
          clinicalStudyId: trialId,
          studySiteId: trialSiteId,
          items: invoiceItems.map((i) => {
            return {
              description: i.description,
              amount: Number(i.amount),
              quantity: Number(i.quantity),
            };
          }),
        };

        const res = await generateFianceInvoice({ body });
        if (res.data) {
          handleClose();
        } else if (res.error) {
          dispatch(
            openMessage({
              messageSeverity: 'error',
              message: `Error generating invoice: ${res.error.message || 'Unknown error'}`,
            })
          );
        }
      }
    } catch (error) {
      console.error('Error generating invoice:', error);

      dispatch(
        openMessage({
          messageSeverity: 'error',
          message: `Failed to generate invoice: ${error.message || 'Unknown error'}`,
        })
      );
    }
  };

  const isInvoiceValid = useMemo(() => {
    return invoiceItems.every(
      (item) =>
        item.description && item.description.trim() !== '' && Number(item.amount) > 0 && Number(item.quantity) > 0
    );
  }, [invoiceItems]);

  // Cancel edit mode if user clicks outside
  const handleCancelEdit = () => {
    if (editIndex !== null) {
      setEditIndex(null);
    }
  };

  return (
    <Card elevation={10}>
      <CardHeader
        title="Invoice"
        titleTypographyProps={{ variant: 'h4', fontWeight: 600 }}
        sx={{ fontWeight: 600, textAlign: 'center' }}
      />
      <CardContent sx={{ px: 5 }} onClick={handleCancelEdit}>
        <Grid container spacing={3}>
          <Grid item xs={5} rowGap={2}>
            <Typography variant="h6" color="initial" sx={{ fontWeight: 600 }}>
              Issuer Information
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Name: &nbsp; <span style={{ fontWeight: 500 }}>{siteInfo?.name || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Address:&nbsp;{' '}
              <span style={{ fontWeight: 500 }}>
                {siteInfo?.address
                  ? `${siteInfo?.address?.line1 || ''}, ${siteInfo?.address?.line2 || ''}, ${
                      siteInfo?.address?.city || ''
                    }`
                  : 'N/A'}
              </span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Phone: &nbsp;<span style={{ fontWeight: 500 }}>{siteInfo?.contact || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Email: &nbsp;<span style={{ fontWeight: 500 }}>{siteInfo?.email || 'N/A'}</span>
            </Typography>
          </Grid>
          <Grid item xs={2} sx={{ pt: 0 }}></Grid>
          <Grid item xs={5}>
            <Typography variant="h6" color="initial" sx={{ fontWeight: 600 }}>
              Recipient Information
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Name: &nbsp; <span style={{ fontWeight: 500 }}>{sponsor?.orgnizationName || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Address:&nbsp; <span style={{ fontWeight: 500 }}>{sponsor?.address || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Phone: &nbsp;<span style={{ fontWeight: 500 }}>{sponsor?.contactNumber || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Email: &nbsp;<span style={{ fontWeight: 500 }}>{sponsor?.contactEmail || 'N/A'}</span>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={7} sx={{ pt: 0 }}></Grid>
          <Grid item xs={4}>
            <Typography variant="h6" color="initial" sx={{ fontWeight: 600 }}>
              Invoice Details
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Invoice Number:&nbsp; <span style={{ fontWeight: 500 }}>{invoiceNumber || 'N/A'}</span>
            </Typography>
            <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600 }}>
              Date:&nbsp; <span style={{ fontWeight: 500 }}>{invoiceDate || 'N/A'}</span>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" color="initial" sx={{ fontWeight: 600, mb: 1 }}>
              Description of Services
            </Typography>
            <TableContainer>
              <Table aria-label="spanning table">
                <CustomTableHead>
                  <TableRow>
                    {columns &&
                      columns.map((col) => (
                        <CustomTableHeadCell
                          key={col.key}
                          align={col.align}
                          sx={{
                            minWidth: col?.minWidth,
                            maxWidth: col?.maxWidth,
                          }}
                        >
                          {col.label}
                        </CustomTableHeadCell>
                      ))}
                  </TableRow>
                </CustomTableHead>
                <TableBody>
                  {invoiceItems.length > 0 ? (
                    invoiceItems.map((row, index) => (
                      <TableRow key={index} onClick={(e) => e.stopPropagation()}>
                        {columns.map((column) => {
                          if (column.key === 'actions' && isEditable) {
                            return (
                              <TableCell align="center" key={column.key} sx={{ py: 1 }}>
                                {editIndex === index ? (
                                  <IconButton
                                    color="success"
                                    onClick={handleSave}
                                    disabled={isLoading || !formData.description || formData.description.trim() === ''}
                                  >
                                    <Check />
                                  </IconButton>
                                ) : (
                                  <IconButton
                                    color="primary"
                                    onClick={() => handleEdit(index)}
                                    disabled={isLoading || editIndex !== null}
                                  >
                                    <Edit />
                                  </IconButton>
                                )}
                                <IconButton
                                  color="error"
                                  onClick={() => handleRemoveRow(index)}
                                  disabled={invoiceItems.length === 1 || isLoading || editIndex !== null}
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            );
                          }
                          if (column.key === 'srno') {
                            return (
                              <TableCell align="center" key={column.key} sx={{ py: 1 }}>
                                {index + 1}
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.key} align={column.align} sx={{ py: 1 }}>
                              {editIndex === index && isEditable ? (
                                <TextField
                                  variant="standard"
                                  value={formData[column.key]}
                                  onChange={(e) => handleChange(e, column.key)}
                                  size="small"
                                  error={
                                    column.key === 'description' &&
                                    (!formData.description || formData.description.trim() === '')
                                  }
                                  helperText={
                                    column.key === 'description' &&
                                    (!formData.description || formData.description.trim() === '')
                                      ? 'Required'
                                      : ''
                                  }
                                  type={column.key === 'amount' || column.key === 'quantity' ? 'number' : 'text'}
                                  InputProps={{
                                    readOnly: column.key === 'total',
                                    inputProps: {
                                      min: column.key === 'amount' || column.key === 'quantity' ? 0 : undefined,
                                      step: column.key === 'amount' ? 0.01 : column.key === 'quantity' ? 1 : undefined,
                                    },
                                  }}
                                  fullWidth
                                />
                              ) : (
                                <>
                                  {column.key === 'amount' || column.key === 'total' ? '$' : ''}
                                  {column.key === 'amount' || column.key === 'total'
                                    ? Number(row[column.key]).toFixed(2)
                                    : row[column.key]}
                                </>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center">
                        No items found. {isEditable && 'Click Add Row to add an item.'}
                      </TableCell>
                    </TableRow>
                  )}
                  {isEditable && (
                    <TableRow sx={{ backgroundColor: theme.palette.grey[50] }} onClick={(e) => e.stopPropagation()}>
                      <TableCell colSpan={columns.length - 1} align="center" sx={{ py: 1 }}></TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleAddRow}
                          disabled={isLoading || editIndex !== null}
                        >
                          Add Row
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Box sx={{ height: 15, width: '100%' }}></Box>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box>
              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Subtotal:
                </Typography>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '30%' }}>
                  <span style={{ fontWeight: 500 }}>$ {subtotal.toFixed(2)}</span>
                </Typography>
              </Box>

              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Taxes (10%):
                </Typography>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '30%' }}>
                  <span style={{ fontWeight: 500 }}>$ {taxes.toFixed(2)}</span>
                </Typography>
              </Box>

              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Stripe Charges (0.25%):
                </Typography>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '30%' }}>
                  <span style={{ fontWeight: 500 }}>$ {stripeCharges.toFixed(2)}</span>
                </Typography>
              </Box>

              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Transaction Handling cost:
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="initial"
                  sx={{ fontWeight: 600, width: '30%', height: '100%', display: 'flex', alignItems: 'flex-end' }}
                >
                  <span style={{ fontWeight: 500 }}>$ {transactionHandlingCost.toFixed(2)}</span>
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ width: '100%', display: 'flex' }}>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '70%' }}>
                  Total Amount Due:
                </Typography>
                <Typography variant="subtitle1" color="initial" sx={{ fontWeight: 600, width: '30%' }}>
                  <span style={{ fontWeight: 500 }}> {`$ ${totalAmountDue.toFixed(2)}`}</span>
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Box sx={{ height: 50, width: '100%' }}></Box>

          {
            <Formik
              initialValues={{
                amendmentType: '',
                reason: '',
              }}
              validationSchema={validationSchema}
              enableReinitialize
              onSubmit={async (values, { setSubmitting }) => {
                try {
                } catch (error) {
                  console.error('API Error:', error);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ values, errors, touched, handleChange, validateForm, setSubmitting, setFieldValue }) => {
                return (
                  <>
                    {isUpdate &&
                      (invoiceData?.invoiceDetails?.status === 'APPROVED' ||
                        invoiceData?.invoiceDetails?.status === 'AMMENDED') && (
                        <>
                          <Grid item xs={4} sx={{ display: 'flex', alignItems: 'end', justifyContent: 'end' }}>
                            <FormControl
                              fullWidth
                              variant="standard"
                              error={touched.amendmentType && Boolean(errors.amendmentType)}
                              //  sx={{ mb: 2 }}
                            >
                              <Select
                                name="amendmentType"
                                value={values.amendmentType}
                                onChange={handleChange}
                                displayEmpty
                              >
                                <MenuItem value="" disabled>
                                  Select amendment type
                                </MenuItem>
                                {[
                                  { label: 'Correction', value: 'correction' },
                                  { label: 'Additional Charge', value: 'additional_charge' },
                                  { label: 'Refund', value: 'refund' },
                                  { label: 'Price Adjustment', value: 'price_adjustment' },
                                  // { label: 'Budget Revision', value: 'Budget Revision' },
                                  // { label: 'Rate Change', value: 'Rate Change' },
                                  // { label: 'Overhead/Indirect Cost Update', value: 'Overhead/Indirect Cost Update' },
                                  // { label: 'Site Addition/Removal', value: 'Site Addition/Removal' },
                                  // { label: 'Patient Count Adjustment', value: 'Patient Count Adjustment' },
                                  // { label: 'Protocol Change Impact', value: 'Protocol Change Impact' },
                                  // { label: 'Data Entry Correction', value: 'Data Entry Correction' },
                                  // { label: 'Billing Error Correction', value: 'Billing Error Correction' },
                                  // { label: 'Tax or Currency Adjustment', value: 'Tax or Currency Adjustment' },
                                  // { label: 'Payment Schedule Change', value: 'Payment Schedule Change' },
                                  // { label: 'Milestone Date Adjustment', value: 'Milestone Date Adjustment' },
                                  // { label: 'Charge Dispute Adjustment', value: 'Charge Dispute Adjustment' },
                                  // { label: 'Credit Note/Refund', value: 'Credit Note/Refund' },
                                  // { label: 'Contract Renegotiation', value: 'Contract Renegotiation' },
                                  // { label: 'New Service Addition', value: 'New Service Addition' },
                                  // { label: 'Pass-Through Cost Inclusion', value: 'Pass-Through Cost Inclusion' },
                                  // { label: 'Billing Detail Update', value: 'Billing Detail Update' },
                                  // { label: 'Invoice Splitting/Merging', value: 'Invoice Splitting/Merging' },
                                  // { label: 'Regulatory Fee Adjustment', value: 'Regulatory Fee Adjustment' },
                                  // { label: 'Compliance Charge Addition', value: 'Compliance Charge Addition' },
                                ].map((type) => (
                                  <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                  </MenuItem>
                                ))}
                              </Select>
                              {touched.amendmentType && errors.amendmentType && (
                                <FormHelperText>{errors.amendmentType}</FormHelperText>
                              )}
                            </FormControl>
                          </Grid>
                          <Grid item xs={4} sx={{}}></Grid>
                          <Grid item xs={4} sx={{}}>
                            <TextField
                              size="medium"
                              label="Reason"
                              variant="standard"
                              name="reason"
                              fullWidth
                              multiline
                              maxRows={3}
                              value={values.reason}
                              onChange={handleChange}
                              error={touched.reason && Boolean(errors.reason)}
                              helperText={touched.reason && errors.reason}
                            />
                          </Grid>
                        </>
                      )}
                    <Box sx={{ height: 20, width: '100%' }}></Box>
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                      <CustomButton
                        variant="outlined"
                        size="small"
                        sx={{ mr: 2 }}
                        disabled={isLoading}
                        onClick={handleClose}
                      >
                        Cancel
                      </CustomButton>
                      {isEditable && (
                        <CustomButton
                          variant="contained"
                          size="small"
                          disabled={isLoading || !isInvoiceValid || editIndex !== null || invoiceItems.length === 0}
                          onClick={() => handleGenerateInvoice(values, validateForm)}
                        >
                          {isUpdate ? 'Update Invoice' : 'Create Invoice'}
                        </CustomButton>
                      )}
                    </Grid>
                  </>
                );
              }}
            </Formik>
          }
        </Grid>
      </CardContent>
    </Card>
  );
};

export default GenerateInvoice;
